import { Link, useLocation } from 'react-router-dom';
import { Home, Compass, PlusCircle, Bell, User, LogOut, Moon, Sun } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Avatar } from '../ui/Avatar';
import { useAuthStore } from '../../stores/auth.store';
import { useUIStore } from '../../stores/ui.store';
import { useNotificationStore } from '../../stores/notification.store';

const navItems = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/discover', icon: Compass, label: 'Discover' },
  { to: '/create', icon: PlusCircle, label: 'Create' },
  { to: '/notifications', icon: Bell, label: 'Alerts' },
  { to: '/profile', icon: User, label: 'Profile' },
];

export function TopNav() {
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-white/10 dark:border-dark-border">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-xl bg-gradient-purple flex items-center justify-center">
            <span className="text-white font-heading font-bold text-sm">C</span>
          </div>
          <span className="font-heading font-bold text-lg gradient-text hidden sm:block">
            Capsula
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink key={item.to} {...item} />
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/notifications" className="relative p-2 rounded-xl hover:bg-cloud dark:hover:bg-dark-surface transition-colors">
            <Bell className="h-5 w-5 text-charcoal dark:text-silver" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-sunrise-coral text-white text-[10px] font-bold flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>

          <button
            onClick={toggleTheme}
            className="p-2 rounded-xl hover:bg-cloud dark:hover:bg-dark-surface transition-colors"
          >
            {theme === 'dark' ? (
              <Sun className="h-5 w-5 text-golden-hour" />
            ) : (
              <Moon className="h-5 w-5 text-charcoal" />
            )}
          </button>

          <div className="flex items-center gap-3 ml-2 pl-3 border-l border-silver/20 dark:border-dark-border">
            <Link to="/profile">
              <Avatar src={user?.avatar_url} name={user?.display_name || user?.handle} size="sm" />
            </Link>
            <button
              onClick={clearAuth}
              className="p-2 rounded-xl hover:bg-cloud dark:hover:bg-dark-surface transition-colors text-silver hover:text-red-500"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function NavLink({ to, icon: Icon, label }: { to: string; icon: typeof Home; label: string }) {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      className={cn(
        'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200',
        isActive
          ? 'bg-capsule-purple/10 text-capsule-purple dark:bg-capsule-purple/20'
          : 'text-charcoal dark:text-silver hover:bg-cloud dark:hover:bg-dark-surface'
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </Link>
  );
}

export function BottomNav() {
  const location = useLocation();
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/10 dark:border-dark-border safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-all relative',
                isActive
                  ? 'text-capsule-purple'
                  : 'text-silver'
              )}
            >
              {item.to === '/create' ? (
                <div className="h-10 w-10 -mt-4 rounded-2xl bg-gradient-purple flex items-center justify-center shadow-glow">
                  <PlusCircle className="h-5 w-5 text-white" />
                </div>
              ) : (
                <item.icon className="h-5 w-5" />
              )}
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.to === '/notifications' && unreadCount > 0 && (
                <span className="absolute -top-0.5 right-0.5 h-3 w-3 rounded-full bg-sunrise-coral" />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
